/**
 * CollectionSearchSelect - Multi-select collection picker with search
 *
 * Used in launch registration requirements config to let creators
 * select one or more collections as requirement targets.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient, ApiResponse } from '../../api/client';

interface CollectionSearchResult {
  id: string;
  name: string;
  image_url: string | null;
  slug: string;
  creator_address: string;
}

interface SelectedCollection {
  id: string;
  name: string;
  image_url: string | null;
}

interface CollectionSearchSelectProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}

export function CollectionSearchSelect({
  selectedIds,
  onChange,
  placeholder = 'Search collections...',
}: CollectionSearchSelectProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CollectionSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState<SelectedCollection[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch collection names for already-selected IDs on mount
  useEffect(() => {
    if (selectedIds.length === 0) {
      setSelectedCollections([]);
      return;
    }

    // Fetch details for selected collections we don't already have
    const missingIds = selectedIds.filter(
      (id) => !selectedCollections.find((c) => c.id === id)
    );
    if (missingIds.length === 0) return;

    const fetchSelected = async () => {
      const existing = selectedCollections.filter((c) => selectedIds.includes(c.id));
      const results = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const response = await apiClient.get<ApiResponse<any>>(
              `/collections/${encodeURIComponent(id)}`
            );
            if (response.success && response.data) {
              return { id: response.data.id, name: response.data.name, image_url: response.data.image_url };
            }
          } catch { /* ignore */ }
          return { id, name: 'Unknown Collection', image_url: null };
        })
      );
      setSelectedCollections([...existing, ...results]);
    };
    fetchSelected();
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const searchCollections = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        limit: '10',
        sortBy: 'name',
      });
      const response = await apiClient.get<ApiResponse<{
        collections: CollectionSearchResult[];
        total: number;
      }>>(`/collections?${params.toString()}`);

      if (response.success && response.data) {
        // Filter out already-selected collections
        setResults(
          response.data.collections.filter((c) => !selectedIds.includes(c.id))
        );
      }
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedIds]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);

    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchCollections(value);
    }, 300);
  };

  const handleSelect = (collection: CollectionSearchResult) => {
    const newSelected: SelectedCollection = {
      id: collection.id,
      name: collection.name,
      image_url: collection.image_url,
    };
    setSelectedCollections((prev) => [...prev, newSelected]);
    onChange([...selectedIds, collection.id]);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const handleRemove = (collectionId: string) => {
    setSelectedCollections((prev) => prev.filter((c) => c.id !== collectionId));
    onChange(selectedIds.filter((id) => id !== collectionId));
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Selected tags */}
      {selectedCollections.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedCollections.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-neutral-border bg-default-background text-body font-body text-default-font"
            >
              {c.image_url && (
                <img
                  src={c.image_url}
                  alt=""
                  className="w-4 h-4 rounded-sm object-cover"
                />
              )}
              <span className="text-sm truncate max-w-[150px]">{c.name}</span>
              <button
                type="button"
                onClick={() => handleRemove(c.id)}
                className="ml-0.5 text-subtext-color hover:text-default-font"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        value={query}
        onChange={handleQueryChange}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-neutral-border bg-default-background text-body font-body text-default-font placeholder:text-subtext-color"
      />

      {/* Dropdown */}
      {isOpen && (query.length >= 2) && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-neutral-border bg-default-background shadow-lg">
          {isLoading && (
            <div className="px-3 py-2 text-caption font-caption text-subtext-color">
              Searching...
            </div>
          )}
          {!isLoading && results.length === 0 && query.length >= 2 && (
            <div className="px-3 py-2 text-caption font-caption text-subtext-color">
              No collections found
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleSelect(c)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-100 text-left transition-colors"
            >
              {c.image_url ? (
                <img
                  src={c.image_url}
                  alt=""
                  className="w-6 h-6 rounded-sm object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-6 h-6 rounded-sm bg-neutral-100 flex-shrink-0" />
              )}
              <span className="text-body font-body text-default-font truncate">
                {c.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
